import type { ColDef, ValueGetterParams } from "@ag-grid-community/core";
import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, noop } from "@regrello/core-utils";
import {
  type FieldInstanceFields,
  FormConstraintConditionOperator,
  type FormFieldFields,
  PropertyDataType,
  type PropertyTypeFields,
  type SpectrumFieldValidationTypeFields,
  type SpectrumValueConstraintFields,
} from "@regrello/graphql-api";
import type { ReactNode } from "react";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

import type { ConfigureSpectrumFieldFormFormFields } from "../../../../views/modals/formDialogs/spectrumFields/_internal/ConfigureSpectrumFieldForm";
import type { CustomFieldPlugin } from "../../../customFields/plugins/types/CustomFieldPlugin";
import { RegrelloFormFieldCheckbox } from "../../../formFields/RegrelloFormFieldCheckbox";
import {
  type ProspectiveSpectrumFieldVersion,
  SpectrumFieldPluginDecorator,
} from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";

type CheckboxFieldPluginFrontendValue = boolean;

export class SpectrumCheckboxFieldPluginDecorator<
  T extends CheckboxFieldPluginFrontendValue,
> extends SpectrumFieldPluginDecorator<T> {
  constructor(plugin: CustomFieldPlugin<T>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.checkbox";
  }

  public canProcessValidationType(spectrumValidationType: Pick<SpectrumFieldValidationTypeFields, "validationType">) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.BOOLEAN;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === PropertyDataType.BOOLEAN);
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.BOOLEAN,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(_valueConstraints: SpectrumValueConstraintFields[]) {
    return EMPTY_ARRAY;
  }

  public getConstraintConditionOperators() {
    return {
      [FormConstraintConditionOperator.EQ]: {
        label: t`Is checked`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.IS_ZERO]: {
        label: t`Is not checked`,
        inputCount: 0,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onChange" as const;
  }

  public renderPreviewFormField() {
    return <RegrelloFormFieldCheckbox disabled={true} onChange={noop} value={false} />;
  }

  public renderValueConstraints(_props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return null;
  }

  public getColumnDef<RowDatum, T>(
    _fieldInstance: FieldInstanceFields,
    _formField: FormFieldFields,
    colDefParams: Partial<ColDef<RowDatum, T>>,
  ): ColDef<RowDatum, T> {
    return {
      ...colDefParams,
      cellClass: "regrello-center-aligned-cell",
      cellDataType: "boolean",
      // eslint-disable-next-line lingui/no-unlocalized-strings
      cellEditor: "agCheckboxCellEditor",
      valueGetter: (params: ValueGetterParams<RowDatum, T>) => {
        const result =
          params.data != null &&
          params.colDef.colId != null &&
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          (params.data as any)?.[params.colDef.colId] === true;
        return result as T;
      },
    };
  }
}
